
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Play, Calendar, Star, Trophy, Users, Share2 } from 'lucide-react';
import { Game } from '@/types/game';
import AdDisplay from '@/components/ads/AdDisplay';
import SharePopoverContent from '@/components/game-detail/SharePopoverContent';

interface GameSidebarProps {
  game: Game;
  onPlay: () => void;
}

const GameSidebar: React.FC<GameSidebarProps> = ({ game, onPlay }) => {
  const displayPlayCount = game.fakePlayCount ?? game.playCount ?? 0;
  
  return (
    <div className="lg:col-span-1 space-y-6">
      <Card className="border-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-xl">
        <CardContent className="p-6">
          <Button
            onClick={onPlay}
            className="w-full bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-6 text-lg shadow-none border-0"
            size="lg"
          >
            <Play className="w-5 h-5 mr-2" fill="currentColor" />
            Play Now
          </Button>
        </CardContent>
      </Card>

      <AdDisplay position="sidebar" className="w-full flex justify-center" />

      <Card className="border-0 bg-white/70 backdrop-blur-sm shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
            Game Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-600 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Category
            </span>
            <span className="font-medium capitalize bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
              {game.category}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-600 flex items-center">
              <Star className="w-4 h-4 mr-2" />
              Rating
            </span>
            <div className="flex items-center space-x-1">
              <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
              <span className="font-medium">{game.rating}/5</span>
            </div>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-600 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Total Plays
            </span>
            <span className="font-medium">{displayPlayCount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Added
            </span>
            <span className="font-medium text-sm">
              {new Date(game.createdAt).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 bg-white/70 backdrop-blur-sm shadow-lg">
        <CardContent className="p-6">
          <div className="space-y-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-start hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Game
                </Button>
              </PopoverTrigger>
              <SharePopoverContent gameTitle={game.title} />
            </Popover>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
export default GameSidebar;
